<template>
  <a-modal
    :title="isLoadActive ? `${hasExistingBid ? 'Update' : 'Submit'} Bid Offer for Load: ${loadTitle}` : `View Bids for Load: ${loadTitle}`"
    :visible="visible"
    width="800px"
    :bodyStyle="{ padding: '16px' }"
    :mask-closable="true"
    :keyboard="true"
    :footer="null"
    @cancel="handleClose"
  >
    <!-- Load Information Header -->
    <a-card size="small" style="margin-bottom: 16px;" v-if="loadInfo && Object.keys(loadInfo).length">
      <template #title>
        <span>Load Information</span>
      </template>
      <a-row :gutter="16">
        <a-col :span="12">
          <p><strong>External Load ID:</strong> {{ (loadInfo && loadInfo.external_load_id) || 'N/A' }}</p>
        </a-col>
        <a-col :span="12">
          <p><strong>Bid ID:</strong> {{ (loadInfo && loadInfo.bid_id) || 'N/A' }}</p>
        </a-col>
      </a-row>
      <a-row :gutter="16">
        <a-col :span="12">
          <p><strong>Origin:</strong> {{ getLocationString(loadInfo && loadInfo.origin_city_name, loadInfo && loadInfo.origin_state_code) }}</p>
        </a-col>
        <a-col :span="12">
          <p><strong>Destination:</strong> {{ getLocationString(loadInfo && loadInfo.destination_city_name, loadInfo && loadInfo.destination_state_code) }}</p>
        </a-col>
      </a-row>
      <a-row :gutter="16">
        <a-col :span="12">
          <p><strong>Distance:</strong> {{ (loadInfo && loadInfo.total_distance) || 'N/A' }} miles</p>
        </a-col>
        <a-col :span="12">
          <p><strong>Pickup Date:</strong> {{ formatDate(loadInfo && loadInfo.scheduled_pickup_date_time) }}</p>
        </a-col>
      </a-row>
    </a-card>

    <!-- User's Previous Bids Section -->
    <a-card size="small" style="margin-bottom: 16px;" title="Your Previous Bids for This Load">
      <div v-if="loadingPreviousBids" class="bid-history-empty">
        <a-spin size="large" />
        <p>Loading your bid history...</p>
      </div>
      
      <div v-else-if="userBids.length === 0 || !userBids" class="bid-history-empty">
        <a-icon type="inbox" />
        <p>No previous bids found for this load</p>
      </div>
      
      <div v-else>
        <a-table
          :columns="bidHistoryColumns"
          :data-source="userBids"
          :pagination="false"
          size="small"
          rowKey="id"
        >
          <!-- Bid Amount Column -->
          <template slot="offer_amount" slot-scope="offer_amount">
            <span style="font-weight: bold; color: #1890ff;">
              ${{ formatCurrency(offer_amount) }}
            </span>
          </template>
          
          <!-- Status Column -->
          <template slot="status" slot-scope="cancelled">
            <a-tag :color="cancelled ? 'red' : 'green'">
              {{ cancelled ? 'Cancelled' : 'Active' }}
            </a-tag>
          </template>
          
          <!-- Created At Column -->
          <template slot="created_at" slot-scope="created_at">
            {{ formatDate(created_at) }}
          </template>
        </a-table>
      </div>
    </a-card>

    <!-- Bid Offer Form - Only show if load is active -->
    <a-card v-if="isLoadActive" size="small" style="margin-bottom: 16px;">
      <template #title>
        <span>{{ hasExistingBid ? 'Update Your Bid' : 'Submit New Bid' }}</span>
      </template>
      
      <!-- Show current bid info if updating -->
      <div v-if="hasExistingBid && currentBidAmount" :style="currentBidCancelled ? 'margin-bottom: 16px; padding: 12px; background-color: #fff2f0; border: 1px solid #ffccc7; border-radius: 6px;' : 'margin-bottom: 16px; padding: 12px; background-color: #f6ffed; border: 1px solid #b7eb8f; border-radius: 6px;'">
        <p :style="currentBidCancelled ? 'margin: 0; color: #ff4d4f;' : 'margin: 0; color: #52c41a;'">
          <a-icon :type="currentBidCancelled ? 'exclamation-circle' : 'info-circle'" style="margin-right: 8px;" />
          <span v-if="currentBidCancelled">
            Your bid of <strong>${{ formatCurrency(currentBidAmount) }}</strong> has been <strong>cancelled</strong>. You can reactivate it by updating the amount below.
          </span>
          <span v-else>
            You currently have a bid of <strong>${{ formatCurrency(currentBidAmount) }}</strong> for this load.
          </span>
        </p>
      </div>
      <a-form :form="form" layout="vertical" @submit="handleSubmit">
        <a-form-item label="Offer Amount ($)" required>
          <a-input-number
            v-decorator="[
              'offer_amount',
              { 
                rules: [
                  { required: true, message: 'Please enter your offer amount!' },
                  { type: 'number', min: 0.01, message: 'Offer amount must be greater than 0!' }
                ]
              }
            ]"
            :min="0.01"
            :step="0.01"
            :precision="2"
            style="width: 100%"
            placeholder="Enter your bid amount"
          />
        </a-form-item>

        <a-form-item label="Notes (Optional)">
          <a-textarea
            v-decorator="[
              'note',
              { rules: [] }
            ]"
            :rows="4"
            placeholder="Add any additional notes or comments about your bid offer"
          />
        </a-form-item>
        
        <!-- Action Buttons -->
        <a-form-item style="margin-bottom: 0; text-align: right;">
          <a-button @click="handleClose" style="margin-right: 8px;">
            Close
          </a-button>
          <a-button 
            v-if="hasExistingBid && !currentBidCancelled" 
            type="danger" 
            @click="handleCancelBid"
            :loading="cancelling"
            style="margin-right: 8px;"
          >
            Cancel Bid
          </a-button>
          <a-button type="primary" html-type="submit" :loading="submitting">
            {{ getSubmitButtonText() }}
          </a-button>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- Message when load is not active -->
    <a-card v-else size="small" style="margin-bottom: 16px;">
      <template #title>
        <span>Bidding Closed</span>
      </template>
      <div style="text-align: center; padding: 20px;">
        <a-icon type="info-circle" style="font-size: 24px; color: #faad14; margin-bottom: 16px;" />
        <p style="margin-bottom: 8px; font-weight: 500;">This load is no longer accepting bids</p>
        <p style="color: #666; margin-bottom: 0;">You can still view your previous bids above, but new bids cannot be submitted.</p>
      </div>
      
      <!-- Close button only -->
      <div style="text-align: right; margin-top: 16px;">
        <a-button @click="handleClose">
          Close
        </a-button>
      </div>
    </a-card>
  </a-modal>
</template>

<script>
import bidAPI from '../../api/bid';

export default {
  name: 'BidOfferPopup',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    loadId: {
      type: String,
      default: null,
    },
    loadInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      form: this.$form.createForm(this, { name: 'bid_offer_form' }),
      submitting: false,
      loadingPreviousBids: false,
      userBids: [],
      cancelling: false,
    };
  },
  computed: {
    loadTitle() {
      if (!this.loadInfo || !this.loadInfo.external_load_id) {
        return 'Unknown Load';
      }
      return this.loadInfo.external_load_id;
    },
    
    // Check if the load is active for bidding
    isLoadActive() {
      return this.loadInfo && this.loadInfo.status === 'active';
    },
    
    // Check if user already has a bid for this load
    hasExistingBid() {
      return this.userBids && this.userBids.length > 0;
    },
    
    // Get the user's current bid amount if they have one
    currentBidAmount() {
      if (this.hasExistingBid && this.userBids[0]) {
        return this.userBids[0].offer_amount;
      }
      return null;
    },
    
    // Check if the current bid is cancelled
    currentBidCancelled() {
      if (this.hasExistingBid && this.userBids[0]) {
        return this.userBids[0].cancelled === true;
      }
      return false;
    },
    
    // Bid history table columns
    bidHistoryColumns() {
      return [
        {
          title: 'Amount',
          dataIndex: 'offer_amount',
          key: 'offer_amount',
          scopedSlots: { customRender: 'offer_amount' },
          width: 120,
          sorter: (a, b) => (a.offer_amount || 0) - (b.offer_amount || 0),
        },
        {
          title: 'Status',
          dataIndex: 'cancelled',
          key: 'status',
          scopedSlots: { customRender: 'status' },
          width: 100,
        },
        {
          title: 'Notes',
          dataIndex: 'note',
          key: 'note',
          ellipsis: true,
          width: 200,
        },
        {
          title: 'Submitted',
          dataIndex: 'created_at',
          key: 'created_at',
          scopedSlots: { customRender: 'created_at' },
          width: 160,
          sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
        },
      ];
    },
  },
  methods: {
    // Format date for display
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    },

    // Get location string
    getLocationString(city, state) {
      if (!city && !state) return 'N/A';
      if (city && state) return `${city}, ${state}`;
      return city || state || 'N/A';
    },

    // Handle form submission
    async handleSubmit(e) {
      e.preventDefault(); // Prevent default form submission
      this.form.validateFields(async (err, values) => {
        if (!err) {
          this.submitting = true;
          try {
            const bidData = {
              load_id: this.loadId,
              offer_amount: values.offer_amount,
              note: values.note || '',
            };

            await bidAPI.createBid(bidData);
            
            // Check if user already had a bid for this load
            let message;
            if (!this.hasExistingBid) {
              message = 'Bid offer submitted successfully!';
            } else if (this.currentBidCancelled) {
              message = 'Bid reactivated successfully!';
            } else {
              message = 'Bid offer updated successfully!';
            }
            
            this.$message.success(message);
            
            // Refresh the bid history to show the updated bid
            await this.fetchUserBids();
            
            // Reset the form only if no existing bids
            if (!this.hasExistingBid) {
              this.form.resetFields();
            }
            
            // Emit event to parent to refresh data if needed
            this.$emit('bid-submitted');
          } catch (error) {
            console.error('Error submitting bid:', error);
            this.$message.error('Failed to submit bid offer. Please try again.');
          } finally {
            this.submitting = false;
          }
        }
      });
    },

    // Handle modal close
    handleClose() {
      // Always reset form when closing
      this.form.resetFields();
      this.userBids = [];
      this.$emit('update:visible', false);
      this.$emit('close');
    },

    // Handle bid cancellation
    async handleCancelBid() {
      if (!this.hasExistingBid || !this.userBids[0]) {
        this.$message.error('No bid found to cancel');
        return;
      }

      const bidId = this.userBids[0].id;
      
      this.$confirm({
        title: 'Cancel Bid',
        content: 'Are you sure you want to cancel your bid? This action cannot be undone and you will not be able to win this load.',
        okText: 'Yes, Cancel Bid',
        okType: 'danger',
        cancelText: 'No, Keep Bid',
        onOk: async () => {
          this.cancelling = true;
          try {
            await bidAPI.cancelBid(bidId);
            this.$message.success('Bid cancelled successfully');
            
            // Refresh the bid history to show the cancelled bid
            await this.fetchUserBids();
            
            // Emit event to parent to refresh data if needed
            this.$emit('bid-cancelled');
          } catch (error) {
            console.error('Error cancelling bid:', error);
            this.$message.error('Failed to cancel bid. Please try again.');
          } finally {
            this.cancelling = false;
          }
        },
      });
    },

    // Fetch user's previous bids for this load
    async fetchUserBids() {
      if (!this.loadId) return;
      
      this.loadingPreviousBids = true;
      try {
        // Fetch all bids for the load
        const response = await bidAPI.getMyBidsByLoadId(this.loadId);
        const allBids = response.bids || [];
        this.userBids = allBids || [];
        
        if (this.userBids && this.userBids.length > 0) {
          this.userBids.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        }
        
      } catch (error) {
        console.error('Error fetching user bids:', error);
        this.userBids = [];
        this.$message.warning('Could not load your bid history');
      } finally {
        this.loadingPreviousBids = false;
      }
    },

    // Format currency for display
    formatCurrency(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    // Get appropriate submit button text based on bid status
    getSubmitButtonText() {
      if (!this.hasExistingBid) {
        return 'Submit Bid';
      }
      
      if (this.currentBidCancelled) {
        return 'Reactivate Bid';
      }
      
      return 'Update Bid';
    },

    // Reset component state
    resetComponentState() {
      this.form.resetFields();
      this.userBids = [];
      this.submitting = false;
      this.loadingPreviousBids = false;
    },


    },
    
    // Watch for changes in loadId to fetch user bids
    watch: {
      loadId: {
        immediate: true,
        handler(newLoadId, oldLoadId) {
          // Reset form when switching to a different load
          if (newLoadId !== oldLoadId) {
            this.resetComponentState();
          }
          
          if (newLoadId && this.visible) {
            this.fetchUserBids();
          }
        },
      },
      visible(newVisible) {
        if (newVisible && this.loadId) {
          this.fetchUserBids();
        } else if (!newVisible) {
          // Reset form when modal is closed
          this.resetComponentState();
        }
      },
      // Watch for changes in userBids to pre-populate form
      userBids: {
        handler(newBids) {
          if (newBids && newBids.length > 0) {
            // Pre-populate form with current bid values
            const currentBid = newBids[0]; // Get the most recent bid
            this.form.setFieldsValue({
              offer_amount: currentBid.offer_amount,
              note: currentBid.note || '',
            });
          } else {
            // Clear form if no bids
            this.form.resetFields();
          }
        },
        immediate: true,
      },
    },
  };
</script>

<style lang="scss" scoped>
.ant-form-item {
  margin-bottom: 16px;
}

.ant-card {
  .ant-card-head {
    min-height: 40px;
    
    .ant-card-head-title {
      font-size: 14px;
      font-weight: 600;
    }
  }
}

.ant-table-small {
  .ant-table-thead > tr > th {
    padding: 8px;
    font-size: 12px;
    font-weight: 600;
  }
  
  .ant-table-tbody > tr > td {
    padding: 8px;
    font-size: 12px;
  }
}

.bid-history-empty {
  text-align: center;
  padding: 20px;
  color: #999;
  
  .anticon {
    font-size: 24px;
    margin-bottom: 8px;
    color: #d9d9d9;
  }
}
</style>
